import { useEffect } from "react";
import type { GetUserQuery } from "graphql/generated";
import { useStores } from "stores";
import { APP_STATE } from "stores/commonStore";
import { prefetchChangelog } from "layouts/navigation/whatsNew/changelogCache";
import { updateStatsigUser } from "utils/statsig";
import {
  getFromStorage,
  NEW_USER_DATA,
  STORAGE_TYPE,
  UTM_PARAMS,
} from "utils/localStorage";

/**
 * The login-time setup. It runs one time for each user. First, it prefetches
 * the changelog. Then it sends the user to Statsig and Userpilot at the same
 * time. When both calls end, it sets `APP_STATE.READY`. The router waits for
 * `APP_STATE.READY` before it renders `AuthLayout`.
 */
export const useSessionBootstrap = (
  user: GetUserQuery["getUser"] | undefined,
) => {
  const { userStore, commonStore } = useStores();

  useEffect(() => {
    if (!user) return;

    // The "What's new" panel and its badge read this cache. A failed prefetch
    // only makes the panel load later.
    prefetchChangelog();

    const { utm_campaign } = getFromStorage<{ utm_campaign?: string }>(
      UTM_PARAMS,
      NEW_USER_DATA,
      {},
      STORAGE_TYPE.LOCAL,
    );

    void Promise.allSettled([
      // The router sets the project's Statsig properties.
      updateStatsigUser({ custom: { utm_campaign: utm_campaign || "" } }),
      userStore.setThirdPartyIdentify({
        id: user.id,
        name: user.name,
        email: user.email,
        created: user.created,
      }),
    ]).then(() => commonStore.setAppState(APP_STATE.READY));
    // Run once for each user, not for each new `GET_USER` answer.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);
};
